import { useEffect, useRef, useState } from 'react'

interface UseGameAudioOptions {
  backgroundMusic?: string
  volume?: number
  loop?: boolean
  autoPlay?: boolean
}

/**
 * Custom hook để quản lý nhạc nền game
 * 
 * @example
 * const { play, pause, setVolume, isPlaying } = useGameAudio({
 *   backgroundMusic: '/sounds/game-background.mp3',
 *   volume: 0.3,
 *   loop: true
 * })
 */
export const useGameAudio = ({
  backgroundMusic,
  volume = 0.3,
  loop = true,
  autoPlay = false
}: UseGameAudioOptions = {}) => {
  const audioRef = useRef<HTMLAudioElement | null>(null)
  const [isPlaying, setIsPlaying] = useState(false)
  const [currentVolume, setCurrentVolume] = useState(volume)

  useEffect(() => {
    if (!backgroundMusic) return

    // Tạo audio element
    const audio = new Audio(backgroundMusic)
    audio.loop = loop
    audio.volume = currentVolume
    audioRef.current = audio

    // Auto play nếu được yêu cầu
    if (autoPlay) {
      const playPromise = audio.play()
      if (playPromise !== undefined) {
        playPromise
          .then(() => setIsPlaying(true))
          .catch((error) => {
            console.log('Auto-play prevented:', error)
            // Browser chặn auto-play, cần user interaction
          })
      }
    }

    // Cleanup khi unmount
    return () => {
      audio.pause()
      audio.currentTime = 0
      audioRef.current = null
    }
  }, [backgroundMusic])

  // Update volume khi thay đổi
  useEffect(() => {
    if (audioRef.current) {
      audioRef.current.volume = currentVolume
    }
  }, [currentVolume])

  // Play music
  const play = () => {
    if (audioRef.current && !isPlaying) {
      const playPromise = audioRef.current.play()
      if (playPromise !== undefined) {
        playPromise
          .then(() => setIsPlaying(true))
          .catch((error) => console.log('Play error:', error))
      }
    }
  }

  // Pause music
  const pause = () => {
    if (audioRef.current && isPlaying) {
      audioRef.current.pause()
      setIsPlaying(false)
    }
  }

  // Toggle play/pause
  const toggle = () => {
    if (isPlaying) {
      pause()
    } else {
      play()
    }
  }

  // Set volume (0-1)
  const setVolume = (vol: number) => {
    const clampedVol = Math.max(0, Math.min(1, vol))
    setCurrentVolume(clampedVol)
  }

  // Stop and reset
  const stop = () => {
    if (audioRef.current) {
      audioRef.current.pause()
      audioRef.current.currentTime = 0
      setIsPlaying(false)
    }
  }

  return {
    play,
    pause,
    toggle,
    stop,
    setVolume,
    isPlaying,
    volume: currentVolume
  }
}

